import { memo, useRef, useMemo, useCallback, useState, useEffect, ReactElement } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Alert, DatePicker, Form, Input, InputNumber, Space } from 'antd';
import dayjs from 'dayjs';

import {
  Crud,
  CrudMode,
  IconButton,
  renderCrudFormSortableGroup,
  notification,
  rejectionError,
  transformCrudFormErrors,
} from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudFormGetDraggableRow, CrudProps, RenderFormItem, ValidationError } from '@ohpen/enso-ui';
import {
  ChangeRequestOfErcProfileCrudRequest,
  ErcProfilesCreateChangeRequestResponse,
  PenaltyTierRequest,
  UsageResponse,
  ercProfilesCheckUsage,
  ercProfilesCreateChangeRequest,
} from '@pmp/api';
import { crudToActionMap, getApiErrorMessage, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import { useFeatureToggle } from '@pmp/hooks';

import { getFormSchema } from '../formConfig';
import type { ErcProfileCrudFromData, ErcProfilesCrudFormProps } from '../interfaces';
import styles from '../styles.module.css';

type ErcBandValidationErrors = {
  order?: string[];
  durationInMonths: string[];
  penaltyPercentage: string[];
  endDate: string[];
};
type ErcValidationErrors = {
  ercProfileId: string[];
  name: string[];
  penaltyTiers: Record<string, ErcBandValidationErrors>;
};

export const ErcProfilesCrudForm = memo(({ mode, record, refreshTable, onClose }: ErcProfilesCrudFormProps): ReactElement | null => {
  const ref = useRef<CrudFormComponent>(null);
  const [open, setOpen] = useState(false);
  const [isUsed, setIsUsed] = useState<boolean | null>(null);
  const { t } = useTranslation();
  const enableFourEyes = useFeatureToggle(['enableFourEyes']);

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (isUsed !== null) return;
    if (mode !== CrudMode.EDIT) {
      setIsUsed(false);
      setOpen(true);
      return;
    }
    if (!record?.ercProfileId) {
      notification().error({ title: t('global.errors.unexpected') });
      handleOnClose();
      return;
    }
    ercProfilesCheckUsage({ path: { ercProfileId: record.ercProfileId } })
      .then(({ data: { isUsed } }: { data: UsageResponse }) => {
        setIsUsed(isUsed);
        setOpen(true);
      })
      .catch(error => {
        notification().error({ title: getApiErrorMessage(error) });
        handleOnClose();
      });
  }, [mode, record, isUsed, t, handleOnClose]);

  const onConfirm: (formData: ErcProfileCrudFromData) => Promise<ValidationError[] | void> = useCallback(
    ({ rollingOrFixed, ...values }) => {
      if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));
      const penaltyTiers = (values.penaltyTiers ?? [{}]).map((band: PenaltyTierRequest, index: number) => ({
        order: index + 1,
        endDate: rollingOrFixed && band.endDate ? dayjs(band.endDate).format('YYYY-MM-DD') : undefined,
        durationInMonths: rollingOrFixed ? undefined : band.durationInMonths,
        penaltyPercentage: band.penaltyPercentage,
      }));
      const body: ChangeRequestOfErcProfileCrudRequest = {
        data: { ...values, penaltyTiers },
        actionType: crudToActionMap[mode],
      };
      return ercProfilesCreateChangeRequest({ body }).then(
        ({ data: { isValid, errors } }: { data: ErcProfilesCreateChangeRequestResponse }) => {
          if (!isValid) {
            const form = ref.current?.form;
            if (form) {
              const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
              if (errorFields.length) form.setFields(errorFields);
            }
            const penaltyTiers = errors?.penaltyTiers
              ? Object.fromEntries(
                  Object.entries(errors.penaltyTiers as ErcValidationErrors['penaltyTiers']).map(([index, penaltyTierErrors]) => {
                    return [
                      index,
                      {
                        ...penaltyTierErrors,
                        durationInMonths: rollingOrFixed ? [] : penaltyTierErrors.durationInMonths,
                        endDate: rollingOrFixed ? penaltyTierErrors.endDate : [],
                      },
                    ];
                  }),
                )
              : {};
            const allErrors = transformCrudFormErrors({ ...errors, penaltyTiers });
            return allErrors;
          }
          if (mode === CrudMode.EDIT) refreshTable();
          notification().success({
            title: changeRequestNotificationMessage(t, enableFourEyes),
          });
        },
      );
    },
    [mode, refreshTable, t, enableFourEyes],
  );

  const getRow: CrudFormGetDraggableRow = useCallback(
    (index, onDeleteRow) => {
      const disabled = mode === CrudMode.VIEW || !!isUsed;
      const fixed = ref.current?.form.getFieldValue(['rollingOrFixed']);

      return (
        <div className={styles.ercBandRow}>
          {fixed ? (
            <div className={styles.ercBandCell}>
              <Form.Item className={styles.ercBandFormItem} name={['penaltyTiers', index as number, 'endDate']} dependencies={['type']}>
                <DatePicker disabled={disabled} placeholder="" />
              </Form.Item>
            </div>
          ) : (
            <div className={styles.ercBandCell}>
              <Form.Item
                className={styles.ercBandFormItem}
                name={['penaltyTiers', index as number, 'durationInMonths']}
                dependencies={['type']}
              >
                <InputNumber disabled={disabled} precision={0} placeholder="" />
              </Form.Item>
            </div>
          )}
          <div className={styles.ercBandCell}>
            <Space.Compact>
              <Form.Item className={styles.ercBandFormItem} name={['penaltyTiers', index as number, 'penaltyPercentage']}>
                <InputNumber disabled={disabled} precision={2} placeholder="" />
              </Form.Item>
              <Input className={styles.currencyPrefix} defaultValue="%" disabled />
            </Space.Compact>
          </div>
          <div className={styles.ercBandDeleteCell}>
            <IconButton icon={<DeleteOutlined />} onClick={() => onDeleteRow?.(index)} disabled={disabled || !onDeleteRow} />
          </div>
        </div>
      );
    },
    [isUsed, mode],
  );

  const renderAlert: RenderFormItem<{ index: number }> = useCallback(
    (_schema, _config, _form) => {
      if (mode === CrudMode.EDIT && isUsed) return <Alert message={t('pmp.erc.labels.ercInUseMessage')} type="warning" showIcon />;
      return null;
    },
    [mode, isUsed, t],
  );

  const renderErcBands: RenderFormItem<{ index: number }> = useCallback(
    (schema, config, form) => {
      const addButton = { label: t('pmp.fees.labels.addBand') };
      const disabled = mode === CrudMode.VIEW || !!isUsed;
      const minLength = 1;
      const fixed = ref.current?.form.getFieldValue(['rollingOrFixed']);

      return (
        <>
          <div className={styles.ercBandsTitles}>
            <div className={styles.ercBandCell}>{fixed ? t('pmp.erc.labels.endDate') : t('pmp.erc.labels.durationInMonths')}</div>
            <div className={styles.ercBandCell}>{t('pmp.erc.labels.ercPercentage')}</div>
            <div className={styles.ercBandDeleteCell} />
          </div>
          {renderCrudFormSortableGroup({
            schema,
            config,
            form,
            addButton,
            getRow,
            disabled,
            minLength,
            setDisableSave: ref.current?.setDisableSave,
          })}
        </>
      );
    },
    [t, mode, isUsed, getRow],
  );

  const title = useMemo(() => {
    if (!mode) return '';
    const titles: { [key: string]: string } = {
      [CrudMode.VIEW]: t('global.labels.viewX', { x: t('pmp.erc.labels.ercProfile') }),
      [CrudMode.CREATE]: t('global.labels.createX', { x: t('pmp.erc.labels.ercProfile') }),
      [CrudMode.EDIT]: t('global.labels.editX', { x: t('pmp.erc.labels.ercProfile') }),
    };
    return titles[mode];
  }, [mode, t]);

  const crudProps: CrudProps<ErcProfileCrudFromData> = useMemo(() => {
    const rollingOrFixed = !!record?.penaltyTiers?.[0]?.endDate;

    return {
      ref,
      open,
      schema: getFormSchema(t, renderErcBands, renderAlert, mode, isUsed),
      title,
      labels: {
        confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
      },
      initialValues: {
        ...record,
        penaltyTiers: record?.penaltyTiers?.map(tier => ({
          ...tier,
          endDate: tier.endDate ? dayjs(tier.endDate) : undefined,
        })),
        rollingOrFixed,
      },
      mode,
      onClose: handleOnClose,
      onConfirm,
    };
  }, [record, open, renderErcBands, renderAlert, mode, isUsed, title, t, handleOnClose, onConfirm, enableFourEyes]);

  return <Crud {...crudProps} />;
});
